// src/context/brandIdentityApi.js
//
// The brand-identity editor's DOCUMENT routes (/api/v1/brand-identity/*), all
// TenantAdmin-gated on the backend (a company admin of the owning customer and a
// superadmin pass too; a plain member gets 403). The Brand Voice is a DIFFERENT entity with its
// own route (contextApi.getBrandVoice) — ⛔ never mix the two, and never share their
// etags: they digest different files.
// ⭐ Every call names its workspace: `?tenant=` + suppressTenantHeader (the handoff:
// a misspelled header is silently ignored and you act on the wrong tenant).
// Errors are built with buildApiError (the envelope is flat): callers branch on
// `err.code` / `err.status` (and may show `err.requestId` as the support
// reference) — ⛔ never `err.message`. Refusals of the read:
// 404 RESOURCE_NOT_FOUND (no brand-guidelines file for this workspace),
// 422 BRAND_GUIDELINES_NOT_TENANT_OWNED (the `default` tenant), 403
// TENANT_ADMIN_REQUIRED. Never 402: editing is free.
// The writes: PUT /document (sparse, stamps `human`), POST /document/lock and
// /document/unlock (ledger rows, `skipped` instead of an error).
import { apiFetch } from '../auth/apiFetch.js';
import { buildApiError } from './parseApiError.js';

const BASE = '/api/v1/brand-identity';

// A falsy tenant THROWS before any request, so a mis-scoped call never reaches the network.
function withTenant(path, tenant) {
  if (!tenant) throw new TypeError('brandIdentityApi: a tenant id is required');
  return `${path}?tenant=${encodeURIComponent(tenant)}`;
}

async function fail(res) {
  const body = await res.json().catch(() => ({}));
  const envelope = body?.detail && typeof body.detail === 'object' ? body.detail : body;
  return buildApiError(envelope, res.status);
}

/**
 * The whole editable document. → { tenant_id, etag, identity, en_half_present,
 * run_active, unlock_effective, fields[], voice }.
 * For `verified_claims` (five ledger rows) a field's `state` is the STRONGEST of the
 * five and `engine_writable` is true if ANY category is.
 */
export async function getBrandDocument({ tenant }) {
  const res = await apiFetch(withTenant(`${BASE}/document`, tenant), { suppressTenantHeader: true });
  if (!res.ok) throw await fail(res);
  return res.json();
}

// ── Lock / unlock: the LEDGER's rows, not the widgets ─────────────────────────
export const CLAIM_CATEGORIES = ['metrics', 'credentials', 'clients', 'case_studies', 'facts'];

// ⚠️ Lock / unlock address the LEDGER's rows, not the widgets: `verified_claims` is one
// widget but five rows, and its bare key would come back in `skipped`. The voice's own
// ledger row is the sentinel path `'voice'`.
export function ledgerPaths(fieldPath) {
  return fieldPath === 'verified_claims'
    ? CLAIM_CATEGORIES.map((c) => `verified_claims.${c}`)
    : [fieldPath];
}

const jsonInit = (method, body, etag) => ({
  method,
  suppressTenantHeader: true,
  // apiFetch already adds `Content-Type: application/json` (it skips it only for FormData).
  headers: etag ? { 'If-Match': etag } : {},
  body: JSON.stringify(body),
});

/**
 * Sparse save. ⛔ `fields` must hold ONLY changed paths (the backend stamps everything it
 * receives as the tenant's). `fieldsEn` only for the three bucket-D paths (a `type_c` path
 * there is 422 BRAND_GUIDELINES_EN_NOT_EDITABLE). Omitted when empty.
 * Refusals write NOTHING, in this order: 422 BRAND_GUIDELINES_NOT_TENANT_OWNED / 404
 * RESOURCE_NOT_FOUND → 422 BRAND_GUIDELINES_FIELD_UNKNOWN → 422
 * BRAND_GUIDELINES_FIELD_INVALID (`details.errors[]`, English prose the caller must NOT
 * render) → 409 RUN_ACTIVE → 503 SERVICE_UNAVAILABLE → 412 CONTEXT_ETAG_MISMATCH.
 * The response carries NO values and the server normalises text → re-GET after a save. Its
 * `outcomes[]` carry the per-field `written` / `state` / `stamped` / `translation` /
 * `translation_reason`.
 */
export async function putBrandDocument({ tenant, etag, fields, fieldsEn, translate }) {
  const body = { fields: fields || {}, translate: !!translate };
  if (fieldsEn && Object.keys(fieldsEn).length) body.fields_en = fieldsEn;
  const res = await apiFetch(withTenant(`${BASE}/document`, tenant), jsonInit('PUT', body, etag));
  if (!res.ok) throw await fail(res);
  return res.json();
}

/**
 * Freeze rows ("the AI will not update them"). → { tenant_id, locked, skipped } — an
 * unknown or unmovable path lands in `skipped`, never in an error. Writes no text, so
 * the document etag does not change.
 */
export async function lockFields({ tenant, fieldPaths }) {
  const res = await apiFetch(
    withTenant(`${BASE}/document/lock`, tenant),
    jsonInit('POST', { field_paths: fieldPaths }),
  );
  if (!res.ok) throw await fail(res);
  return res.json();
}

/** The inverse: hand the rows back to the AI. → { tenant_id, unlocked, skipped }. */
export async function unlockFields({ tenant, fieldPaths }) {
  const res = await apiFetch(
    withTenant(`${BASE}/document/unlock`, tenant),
    jsonInit('POST', { field_paths: fieldPaths }),
  );
  if (!res.ok) throw await fail(res);
  return res.json();
}
